import { type PaymentAccountCardType } from "@/features/payments/types/payment-account-cards";

import { type Transaction } from "./card-transactions.page";

export function groupAndSortByDate<T>(
  items: T[],
  dateKey: keyof T
): Record<string, T[]> {
  // Group items by calendar date (YYYY-MM-DD)
  const groupedItems = items.reduce(
    (acc, item) => {
      const dateValue = item[dateKey];

      if (dateValue == null) {
        return acc;
      }

      // Extract date string (support both Date objects and ISO strings)
      let dateStr: string;
      if (dateValue instanceof Date) {
        dateStr = dateValue.toISOString().split("T")[0];
      } else if (typeof dateValue === "string") {
        dateStr = dateValue.split("T")[0];
      } else {
        return acc; // Skip invalid date types
      }

      if (!acc[dateStr]) {
        acc[dateStr] = [];
      }
      acc[dateStr].push(item);

      return acc;
    },
    {} as Record<string, T[]>
  );

  // Sort dates descending (no need to rebuild object)
  return Object.keys(groupedItems)
    .sort((a, b) => b.localeCompare(a)) // Lexicographic sort for YYYY-MM-DD
    .reduce(
      (acc, date) => {
        acc[date] = groupedItems[date];
        return acc;
      },
      {} as Record<string, T[]>
    );
}

/**
 * The list holds every transaction for the participant. Stripe rows name their
 * card or account in `payment_type` (`VIRTUAL_SPEND_CARD`, `BANK_ACCOUNT`, ...),
 * which is the route slug in upper snake case. Highnote rows have no
 * `payment_type` and are kept as before.
 */
export function filterTransactionsForCard(
  transactions: Transaction[],
  cardType: PaymentAccountCardType | undefined
): Transaction[] {
  if (!cardType) {
    return transactions;
  }
  const paymentType = cardType.toUpperCase().replace(/-/g, "_");
  return transactions.filter(
    (transaction) =>
      !transaction.payment_type || transaction.payment_type === paymentType
  );
}

/** Stripe card rows where no money left the card. */
export function isNoMoneyMoved(transaction: Transaction) {
  const type = transaction.type.toLowerCase();
  return type === "declined" || type === "reversed";
}

export function getAmount(transaction: Transaction) {
  if (transaction.type === "Credit" || isNoMoneyMoved(transaction)) {
    return transaction.amount;
  }
  return Number(transaction.amount) > 0
    ? `-${transaction.amount}`
    : transaction.amount;
}

export function getTransactionTitleByType(transaction: Transaction) {
  const type = transaction.type.toLowerCase();
  if (type === "debit") {
    return "debit_transaction";
  } else if (type === "credit") {
    return "deposit_received";
  }
  return "transaction";
}
